import React, {Component, Fragment} from "react";
import * as PropTypes from "prop-types";
import _, {debounce, sortBy} from "lodash";
import Toolbar from "../../publishing/postActions/Toolbar";
import SocialImage from "../../../../components/socialimage/SocialImage";
import ActivityStreamText from "./ActivityStreamText";
import ActivityStreamImageAttachment from "./ActivityStreamImageAttachment";
import "./conversationItem.scss";
import {replies} from "../postsApi";
import {stallUnresolvedPromises} from "../../../../util/async";
import SocialReplyList from "./SocialReplyList";
import PostResponder from "../../publishing/postActions/PostResponder";
import PublishingCredentialStore from "../../publishing/PublishingCredentialStore";
import socialPagesStore from "../../publishing/SocialPagesStore";

import {observer} from "mobx-react"
import {AuthorType} from "./postMetaData/AuthorType";
import WorkflowSummaryLink from "../../../workflow/task/WorkflowSummaryLink";
import SocialReplyEditor from "./SocialReplyEditor";
import SocialPublishingStatus from "./postMetaData/SocialPublishingStatus";
import PostHeader from "./postMetaData/PostHeader";


const PAGING_SIZE = 10
/**
 * Component to render a reply (or nested reply) to a social post
 *
 * This is a pretty confusing component, used recursively.
 * It has some conditional scenarios where it doesn't render the actual post.
 * e.g. this component gets handed the root component in order to render
 * the responding toolbar, and the root post's replies
 *
 */
@observer
export default class SocialReplyItem extends Component {

  static propTypes = {
    item: PropTypes.any.isRequired,
    /** path to the selected post, from child id to rootmost post */
    ascendingReplyPath: PropTypes.array,
    grabScrollPosition: PropTypes.func,
    restoreScrollPosition: PropTypes.func,
    onUpdate: PropTypes.func,
    onApprove: PropTypes.func,
    onReject: PropTypes.func,
    onDelete: PropTypes.func,
    hideWorkflowTask: PropTypes.bool,

    // The index of the post, i.e. for being able to determine the color
    index: PropTypes.number.isRequired,
    depth: PropTypes.number.isRequired
  };

  static defaultProps = {
    ascendingReplyPath: [],
    grabScrollPosition: () => {},
    restoreScrollPosition: () => {},
    hideWorkflowTask: false
  };


  state = {
    isLoading: false,
    isLoadingOlder: false,
    isLoadingNewer: false,
    offsetOdd: false,
    showOptionToReply: false,
    showLoadedReplies: false
  };

  // TODO - currently stomps over state when this component reloads from publishing. Less than ideal.
  // would be nice if a reply item could reload just its current state tree

  constructor(props) {
    super(props);

    const ascendingReplyPath = this.props.ascendingReplyPath;
    const remoteId = this.props.item.remoteId;
    const ancestors = ascendingReplyPath.slice(0, ascendingReplyPath.length - 1);
    const overrideShowChildren = ancestors.includes(remoteId);
    this.state.showLoadedReplies = overrideShowChildren;

  }

  componentWillUnmount() {
    /*
     * When making async calls that call setState, the component could be unmounted,
     * this is a hacky toggle to ensure that it is mounted.
     */
    this.isCancelled = true;
  }

  loadReplies = stallUnresolvedPromises(replies);


  /** loads replies to this item */
  loadChildren({ loadOlder = false }) {
    if (!this.props.disableLoadChildren) {

      const {item} = this.props;
      const conversation = item.conversation;
      let query = {query: {postId: item.postId}, limit: PAGING_SIZE};

      if (loadOlder && !!conversation.older) {
        query = {...query, pagination: conversation.older}
      } else if (!!conversation.newer) {
        query = {...query, pagination: conversation.newer}
      }

      if (!this.isCancelled) {
        this.loadReplies(query).then((data) => {

          const grabbed = this.props.grabScrollPosition({ top: loadOlder });

          // post arrays should be in descending order (here and in loaded replies response, no matter the paging direction)
          const updated = {
            ...item,
            conversation: {
              ...conversation,
              ...(loadOlder ? {
                replies: conversation.replies.concat(data.replies),
                older: data.hasNext && data.next || undefined,
              } : {
                replies: data.replies.concat(conversation.replies),
                newer: data.hasNext && data.next || undefined
              })
            }
          }

          this.props.onUpdate(updated)

          this.setState(loadOlder ? {
            offsetOdd: data.replies.length % 2 === 1 ? !this.state.offsetOdd : this.state.offsetOdd,
            isLoadingOlder: false
          } : {
            isLoadingNewer: false
          }, () => {
            // restore the scroll position
            this.props.restoreScrollPosition(grabbed);
            // then restore again in 500ms in case some images in the replies just loaded.
            // Past that duration seems like it would be jarring unless we can react directly to the image
            // changing the container size
            setTimeout(() => {
              const grabbedAgain = this.props.grabScrollPosition({ top: loadOlder });
              setTimeout(() => this.props.restoreScrollPosition(grabbedAgain), 500);
            }, 100)
          });

        });
      }
    }
  };


  loadNewer = debounce(() => {
    this.setState({ isLoadingNewer: true });
    this.loadChildren({ loadOlder: false });
  }, 500);
  loadOlder = debounce(() => {
    this.setState({ isLoadingOlder: true });
    this.loadChildren({ loadOlder: true });
  }, 500);



  /** just fills in a username/avatar for the pending replies since those aren't filled in.
   * Probably something the api should do
   **/
  enhancePostToBePublishedWithCredentials() {
    const item = this.props.item;
    if (!item.isUnpublished) { // if published, should be good to go
      return item;
    } else {
      const publishing = item.postToBePublished;
      const listingsByRemoteId = !!publishing.remotePageId && socialPagesStore.page(item.provider, publishing.remotePageId);
      const chatterUser = !!publishing.chatterUserAccountId && PublishingCredentialStore.credentialById(item.accountId, publishing.chatterUserAccountId);
      let extraAccountInfo = {};
      if (!!chatterUser) {
        extraAccountInfo = {
          username: (!!listingsByRemoteId && listingsByRemoteId.name) || chatterUser.userDetails.fullName,
          avatar: !listingsByRemoteId && chatterUser.userDetails.profilePicture
        };
      }
      return {...item, ...extraAccountInfo };
    }
  }

  onChildUpdate = updatedChild => {
    this.props.onUpdate({
      ...this.props.item,
      conversation: {
        replies: this.props.item.conversation.replies.map(child => {
          const same = child.postId && updatedChild.postId === child.postId ||
            child.socialPublishingId && updatedChild.socialPublishingId === child.socialPublishingId;
          return same ? updatedChild : child
        })
      }
    })
  };

  render() {
    const {
      index,
      depth,
      grabScrollPosition,
      restoreScrollPosition,
      ascendingReplyPath,
      item,
      hideWorkflowTask
    } = this.props;
    const {
      isLoadingOlder, isLoadingNewer, showLoadedReplies, showOptionToReply,
      offsetOdd
    } = this.state;


    const children = this.props.item.conversation.replies;

    const post = this.enhancePostToBePublishedWithCredentials();

    const childCount = !!item.conversation && item.conversation.replies.length;

    const oddRow = !(index % 2);
    const mainColor = oddRow ? "odd" : "even";
    const remoteId = item.remoteId;

    const showChildren = !!children && children.length > 0 && (depth <= 0 || showLoadedReplies);
    const hasPostsToShow = (childCount > 0 || (!!children && children.length > 0));

    const text = !!post && !!post.details ? post.details.text : post.text;
    const attachments = post.isUnpublished && !!post.details ? post.details.attachments : post.attachments;

    const rootOrNestedModifier = depth < 1 ? 'root' : depth > 1 ? 'nested' : '';

    const pointOfInterestModifier = depth > 0 && ascendingReplyPath[0] === remoteId ? ' highlight-area' : '';
    const isEmptyText = !!text ? "" : "empty-text";

    let shouldShowWorkflowTask = !hideWorkflowTask
      && !!post.workflowResponseTask
      && (
        ["Unapproved", "PendingApproval"].includes(post.workflowResponseTask.content.status) ||
        //if a task is complete, but has rejected posts show the workflow
        post.scheduledResponses.some(resp => resp.requiresApproval || resp.rejectedForPublishing)
      );


    return (
        <div
          id={remoteId}
          data-post-id={item.postId}
          className={`post-conversation-item ${mainColor} ${rootOrNestedModifier} ${isEmptyText} ${pointOfInterestModifier}`}
        >

          { /* Post Details */}
          { depth > 0 &&

            <div className={`post-conversation-item-details`}>

              { /* Post Icon & Depth Indicator */}
              <div className={`depth-icon`}>
                {depth > 1 &&
                  <i className="conversation-item-depth-reply ss ss-replyall"/>
                }
                <SocialImage
                  size={30}
                  userImage={post.avatar}
                />
              </div>


              { /* Post Content */}
              <div className={"conversation-item-content"} >

                <div className={"post-content-meta-data"} >
                  <PostHeader post={post}/>
                  {!!post.authorType && <AuthorType post={post}/>}
                </div>

                { !!text && <ActivityStreamText text={text}/> }

                {!!attachments && attachments.length > 0 &&
                  <div className="attachments post-content-attachments">
                    {attachments.map(attachment => {
                      return (
                        <ActivityStreamImageAttachment key={attachment.url} img={attachment.url} link={post.url}/>
                      );
                    })}
                  </div>
                }

                {depth <= 1 && !post.isUnpublished &&
                  <div className="conversation-item-toolbar">
                    <Toolbar
                      item={post}
                      isPostDetails={true}
                      hiddenToolbarItems={['reply-count']}
                      onReply={() => this.setState(state => ({showOptionToReply: !state.showOptionToReply}))}
                    />
                  </div>
                }
                <div>
                  <SocialPublishingStatus post={post}/>
                </div>
                <SocialReplyEditor
                  item={post}
                  onApprove={this.props.onApprove}
                  onReject={this.props.onReject}
                  onDelete={this.props.onDelete}
                  onUpdate={(updatedItem) => this.props.onUpdate(updatedItem)}
                />

                { /* Response */ }
                { depth > 0 && depth < 2 && showOptionToReply && !post.isUnpublished &&
                  <div className={"conversation-item-children"}>{
                    <PostResponder
                      hidePostsBeingPublished={true}
                      item={post}
                      onResponseSaved={(added) => {
                        const item = this.props.item
                        return this.props.onUpdate({...item,
                          scheduledResponses:
                            (item.scheduledResponses || []).concat([added.postToBePublished]),
                          conversation: {
                            ...item.conversation,
                            replies: item.conversation.replies.concat([added])
                          }
                        });
                      }}
                      placeholder={
                        item.provider === 'Google' ? 'Write a new Answer' : undefined
                      }
                      hideNotes={true}
                    />
                  }</div>
                }



                { /* Toggle Hide/Show Replies */ }
                { hasPostsToShow && depth > 0 && !post.isUnpublished &&
                  <div className={'toggle-replies'} onClick={() => {
                    return this.setState({showLoadedReplies: !showLoadedReplies})
                  }}>
                    <span className={"info secondary"} >{ showLoadedReplies ? 'Hide Replies' : 'Show Replies' }</span>

                    <span className={"info secondary toggle-replies-icon"} >
                      { showLoadedReplies ? <i className={'ss-navigatedown'}/> : <i className={'ss-navigateup'} /> }
                    </span>
                  </div>
                }

              </div>
              { /* End of Post Content */}
            </div>
          }
          { /* End Of Post Details */}

          {  /* Children */ }
          { showChildren && !post.isUnpublished &&
            <SocialReplyList
              children={sortBy(children, x => x.createdTime)} // post array is in descending order
              offsetOdd={offsetOdd}
              ascendingReplyPath={ascendingReplyPath}
              grabScrollPosition={grabScrollPosition}
              restoreScrollPosition={restoreScrollPosition}
              loadOlder={!!post.conversation.older ? this.loadOlder : undefined}
              loadNewer={!!post.conversation.newer ? this.loadNewer : undefined}
              depth={depth}
              index={this.props.index} // Use the props one, not breakpoint one
              isLoadingNewer={isLoadingNewer}
              isLoadingOlder={isLoadingOlder}
              onApprove={this.props.onApprove}
              onReject={this.props.onReject}
              onDelete={this.props.onDelete}
              onChildUpdate={this.onChildUpdate}
              hideWorkflowTask={this.props.hideWorkflowTask}
              scheduledResponses={item.scheduledResponses}
            />
          }
          {
            shouldShowWorkflowTask && <div className={`post-conversation-item-task-link ${rootOrNestedModifier}`}>
              <WorkflowSummaryLink workflowTask={post.workflowResponseTask} />
            </div>
          }

        </div>
    )

  }


}
